import {Component, Prop, Vue} from 'vue-property-decorator';
import {Amounts, ClaimType, ProductType, SelectListItem} from '@/types/claim';
import { ClaimOption, EnumsType, GetClaimsNo, PgType } from 'supporter';
import {formatCurrency} from '@/utils/formatUtils';
import { namespace } from 'vuex-class';

const orderEnumsModule = namespace('orderEnums');

@Component({
  filters: {
    comma: formatCurrency,
    minusComma(value: number): string {
      if (value === 0) {
        return '0';
      }
      return (Number(value) * -1).toLocaleString();
    }
  }
})
export default class AmountMixin extends Vue {
  @orderEnumsModule.Getter('pgType')
  private readonly pgType!: EnumsType[];

  @Prop({required: true})
  protected claimType!: ClaimType;

  @Prop({required: true})
  protected claimNoResponseData!: GetClaimsNo;

  protected amounts: Amounts = {} as any;
  protected refundSelectList: SelectListItem[] = [];

  protected created() {
    this.setAmounts();
  }

  private setAmounts() {
    this.amounts = this.claimNoResponseData.claimData.amounts;
  }

  protected onlyCommaNumber(event: Event) {
    const $input = event.target as HTMLInputElement;

    if ($input.value === '' || $input.value === '0') {
      $input.value = '0';
      return;
    }

    $input.value = $input.value
      .toString()
      .replace(/\D/g, '')
      .replace(/^0+/, '')
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // https://nhnent.dooray.com/project/2684071286150224968/2711625814921673057?query=all%3D%25EC%259C%2584%25ED%2583%2581%25EB%25B0%25B0%25EC%2586%25A1
  protected generateRefundTypeSelectList(adjustedAmounts, pgType: PgType, refundPayTypeLabel: string) {
    let refundSelectList = [];

    // 환불금액이 없으면 ZERO_REFUND 추가
    if (adjustedAmounts.claimAmt === 0) {
      refundSelectList.push({value: 'ZERO_REFUND', name: this.$t('CLAIM_COMMON_ZERO_REFUND')});
    }
    // 환불금액이 적립금반환만 있는 경우
    if (adjustedAmounts.mainPayAmt === 0 && adjustedAmounts.accumulationPayAmt > 0) {
      refundSelectList.push({value: 'ACCUMULATION', name: this.$t('CLAIM_COMMON_ACCUMULATION')});
    }
    // 카드취소/현금환불액이 있는 경우
    if (adjustedAmounts.mainPayAmt > 0) {
      // 무통장은 무조건 추가
      refundSelectList.push({value: 'ACCOUNT', name: this.$t('CLAIM_COMMON_ACCOUNT')});

      // PG를 사용한 경우 PG의 라벨 - [payTypeLabel]
      if (pgType != 'NONE') {
        const selectedPgType = this.pgType.find(pgTypeItem => pgTypeItem.name === pgType);
        refundSelectList.push({value: 'PG', name: `${selectedPgType.label}[${refundPayTypeLabel}]`});
      }
    }

    return refundSelectList;
  }

  // TODO: BPC 판매할인 적용가 수식 다시 살펴봐야할 듯
  // 전체 취소일 경우 여러 옵션상품이 출력될 수 있음. 그래서 따로 파라미터를 받아 계산하는 메서드로 만듬
  protected immediateDiscountedAmt({claimedOption, needSum = false}: {claimedOption: ClaimOption; needSum: boolean}): number {
    const {salePrice, addPrice, immediateDiscountAmt, orderCnt} = claimedOption;
    const immediateDiscountedAmt = salePrice + addPrice - immediateDiscountAmt;

    return needSum ? immediateDiscountedAmt * orderCnt : immediateDiscountedAmt;
  }

  // 즉시할인이 적용된 구매금액(교환상품, 취소상품) immediateDiscountedAmt
  // https://nhnent.dooray.com/project/2684071286150224968/2711586720074695171?contentsType=contents&query=all%3D%25EC%25B7%25A8%25EC%2586%258C&tags=and,2708541498271128037&page=1
  protected exchangeImmediateDiscountedAmt({type, needSum = false}: {type: ProductType.EXCHANGE_PRODUCT | ProductType.CLAIMED_PRODUCT; needSum: boolean}) {
    const FIRST_ITEM = 0; // 교환은 1:1 대응임. 전체교환 없음. 그래서 무조건 첫번째 아이템만 있음
    const {salePrice, addPrice, immediateDiscountAmt, orderCnt, adjustedAmt} = type === ProductType.EXCHANGE_PRODUCT ? this.claimNoResponseData.exchangeOption : this.claimNoResponseData.claimedOptions[FIRST_ITEM];

    const immediateDiscountedAmt = salePrice + addPrice - immediateDiscountAmt;
    return needSum ? adjustedAmt : immediateDiscountedAmt;
  }

  protected isZero(amount: number): boolean {
    return amount === 0;
  }
}
